'use client';

import {motion} from 'framer-motion';
import styles from './styles.module.scss';
import {useState, useEffect, useMemo} from 'react';
import {handleLogin, handleUserInfo, loginState} from '@/utils/event/util';
import globalDomains, {globalAPPScheme} from '@/utils/global-domains';
import SubsDirectChangeApi from '@/api/events/subs-direct-change';
import MyProfileImg from '@/components/my/common/item/MyProfileImg';
import {getDeviceType} from '@/utils';
import LogApi from '@/api/common/log';

const DATA_STATUS = {
  EVENT_FINISHED: 'event_finished',
  NOT_LOGIN: 'not_login',
  NONE: 'none',
  OK: 'ok',
};

const EVENT_END_DATE = new Date('2025-10-15T23:59:59');
const EVENT_START_DATE = new Date('2025-07-22T00:00:00');

const SubscribeDirectChange = ({onBodyId}) => {
  const [dataStatus, setDataStatus] = useState(DATA_STATUS.NOT_LOGIN);
  const [subsItem, setSubsItem] = useState([]);

  const [activeGuideLines, setActiveGuideLines] = useState(-1);
  const [activeTab, setActiveTab] = useState(1);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const {userId, userNickname} = useMemo(() => handleUserInfo(), []);
  /** 이벤트 기간 여부 */
  const isEventActive = useMemo(() => new Date() < EVENT_END_DATE, []);
  const deviceType = getDeviceType();

  // 타이머 계산
  const calculateTimeLeft = () => {
    const targetDate = new Date('2025-10-15T23:59:59');
    const now = new Date();
    const difference = targetDate - now;

    if (difference > 0) {
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({days, hours, minutes, seconds});
    } else {
      setTimeLeft({days: 0, hours: 0, minutes: 0, seconds: 0});
    }
  };

  const getSubsList = async () => {
    try {
      if (!isEventActive) {
        setDataStatus(DATA_STATUS.EVENT_FINISHED);
        return;
      }
      if (!loginState) {
        setDataStatus(DATA_STATUS.NOT_LOGIN);
        return;
      }
      if (loginState) {
        const data = {
          userId: userId,
        };
        const res = await SubsDirectChangeApi.getDirectChangeTargetList(data);
        if (res.retcode === 0) {
          const list = res.data;
          if (list.length === 0) {
            setDataStatus(DATA_STATUS.NONE);
          } else {
            setDataStatus(DATA_STATUS.OK);
            setSubsItem(list);
          }
        } else if (res.retcode === 1001) {
          setDataStatus(DATA_STATUS.EVENT_FINISHED);
        } else {
          console.log(res.retcode);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleClickLog = (buttonType) => {
    LogApi.sendClickLog({
      codeType: 'change_subscription_prom',
      sendData: {
        buttonType: buttonType,
      },
    });
  };

  const handleActiveGuideLine = (event) => {
    event.preventDefault();
    let activeNo =
      activeGuideLines === parseInt(event.currentTarget.value)
        ? -1
        : parseInt(event.currentTarget.value);

    setActiveGuideLines(activeNo);
  };

  const handleTabClick = (tabNumber) => {
    if (tabNumber === 1) {
      handleClickLog('tab_change_direct_go');
    } else if (tabNumber === 2) {
      handleClickLog('tab_first_subauto');
    }

    const targetElement = document.querySelector(
      `.${styles[`tab${tabNumber}`]}`,
    );
    if (targetElement) {
      const elementTop = targetElement.offsetTop;
      const isMobile = window.innerWidth <= 768;
      const offset = isMobile ? 32 : 64; // 768px 이하에서는 32px, 그 이상에서는 64px
      window.scrollTo({
        top: elementTop - offset,
        behavior: 'smooth',
      });
    }
  };

  const handleClickSubsBtn = () => {
    handleClickLog('change_direct_go');

    if (deviceType === 'pc') {
      window.open(
        `${globalDomains.POINT_AFREECATV}/subscription/subscriptionList.asp`, // 열고자 하는 URL
        '_blank',
      );
    } else {
      const scheme = `${globalAPPScheme}go/subscription`;

      let hidden = false;
      const handler = () => {
        hidden = document.hidden;
      };
      document.addEventListener('visibilitychange', handler);
      window.location.href = scheme;

      setTimeout(() => {
        document.removeEventListener('visibilitychange', handler);

        if (!hidden) {
          window.afreeca?.link?.goStore();
        }
      }, 3000);
    }
  };

  const handleClickItemBtn = () => {
    let url =
      deviceType === 'pc'
        ? `${globalDomains.ITEM_AFREECATV}/subscription.php#benefit_area`
        : `${globalDomains.M_AFREECATV}/notice/a/view/no/8329/platform/mobileweb`;

    window.open(
      url, // 열고자 하는 URL
      '_blank',
    );
  };

  const handleClickPersonalBtn = (e) => {
    e.preventDefault();

    handleClickLog('personalinformation');

    let url =
      deviceType === 'pc'
        ? `${globalDomains.MEMBER_AFREECATV}/app/user_info.php`
        : `${globalDomains.AUTH_M_AFREECATV}/myinfo/confirmPwd`;

    window.open(
      url, // 열고자 하는 URL
      '_blank',
    );
  };

  // 스크롤 위치 감지하여 active 탭 설정
  useEffect(() => {
    const handleScroll = () => {
      const tab1Element = document.querySelector(`.${styles.tab1}`);
      const tab2Element = document.querySelector(`.${styles.tab2}`);

      if (tab1Element && tab2Element) {
        const tab1Top = tab1Element.offsetTop;
        const tab2Top = tab2Element.offsetTop;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const isMobile = window.innerWidth <= 768;
        const offset = isMobile ? 32 : 64;

        // 페이지 맨 아래에 도달했는지 확인
        const documentHeight = document.documentElement.scrollHeight;
        const windowHeight = window.innerHeight;
        const isAtBottom = scrollTop + windowHeight >= documentHeight - 10; // 10px 여유

        if (isAtBottom) {
          setActiveTab(2);
        } else if (scrollTop >= tab2Top - offset) {
          setActiveTab(2);
        } else if (scrollTop >= tab1Top - offset) {
          setActiveTab(1);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 타이머 업데이트
  useEffect(() => {
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    getSubsList();

    return () => clearInterval(timer);
  }, []);

  return (
    <div id={styles.wrap}>
      {/* header */}
      <header className={styles.header}>
        <div className={styles.inn}>
          <h1>
            <a
              href='#'
              onClick={() => {
                if (window.afreeca.link.isWebview()) {
                  window.location.reload();
                  return;
                }
                window.location.href = `${globalDomains.WWW_AFREECATV}`;
                return;
              }}>
              SOOP
            </a>
          </h1>
          <div className={styles.login_wrap}>
            {loginState ? (
              <>
                <div className={styles.user}>
                  <MyProfileImg userId={userId} className={styles.thumb} />
                </div>
                <button
                  type='button'
                  className={styles.logout}
                  onClick={handleLogin}>
                  로그아웃
                </button>
              </>
            ) : (
              <button
                type='button'
                className={styles.login}
                onClick={handleLogin}>
                {loginState ? '로그아웃' : '로그인'}
              </button>
            )}
          </div>
        </div>
      </header>
      {/* header */}

      {/* visual */}
      <div className={styles.visual}>
        <div className={styles.inn}>
          <motion.div
            className={styles.title_head}
            initial={{opacity: 0, x: 50}}
            whileInView={{opacity: 1, x: 0}}
            viewport={{once: true}}
            transition={{
              ease: 'easeInOut',
              duration: 0.4,
            }}>
            <strong>추가 결제 0원,</strong>
            <span>플러스 업그레이드</span>
          </motion.div>
          <motion.h2
            className={styles.title}
            initial={{opacity: 0, x: 50}}
            whileInView={{opacity: 1, x: 0}}
            viewport={{once: true}}
            transition={{
              ease: 'easeInOut',
              duration: 0.4,
              delay: 0.2,
            }}>
            구독 즉시 변경 할래 말래?
          </motion.h2>
          <motion.span
            className={styles.event_date}
            initial={{opacity: 0, x: 50}}
            whileInView={{opacity: 1, x: 0}}
            viewport={{once: true}}
            transition={{
              ease: 'easeInOut',
              duration: 0.4,
              delay: 0.2,
            }}>
            25. 09. 16(화) ~ 25. 10. 15(수)
          </motion.span>
          <motion.div
            className={styles.title_desc}
            initial={{opacity: 0, x: 50}}
            whileInView={{opacity: 1, x: 0}}
            viewport={{once: true}}
            transition={{
              ease: 'easeInOut',
              duration: 0.4,
              delay: 0.2,
            }}>
            <p>
              구독 베이직을 플러스 자동 결제로 신청하면, <em>단 1번</em>
            </p>
            <strong>
              플러스로 <em>무료 즉시 업그레이드!</em>
            </strong>
          </motion.div>
          <motion.div
            className={styles.timer_box}
            initial={{opacity: 0, x: 50}}
            whileInView={{opacity: 1, x: 0}}
            viewport={{once: true}}
            transition={{
              ease: 'easeInOut',
              duration: 0.4,
              delay: 0.4,
            }}>
            <p className={styles.desc}>
              <em>플러스 즉시 업그레이드</em> 종료까지 ⏰
            </p>
            <div className={styles.time}>
              <div className={styles.box}>
                <strong>{String(timeLeft.days).padStart(2, '0')}</strong>
                <span>일</span>
              </div>
              <i className={styles.colon}>:</i>
              <div className={styles.box}>
                <strong>{String(timeLeft.hours).padStart(2, '0')}</strong>
                <span>시간</span>
              </div>
              <i className={styles.colon}>:</i>
              <div className={styles.box}>
                <strong>{String(timeLeft.minutes).padStart(2, '0')}</strong>
                <span>분</span>
              </div>
              <i className={styles.colon}>:</i>
              <div className={styles.box}>
                <strong>{String(timeLeft.seconds).padStart(2, '0')}</strong>
                <span>초</span>
              </div>
            </div>
          </motion.div>
          <motion.div
            className={styles.gift_desc}
            initial={{opacity: 0, x: 50}}
            whileInView={{opacity: 1, x: 0}}
            viewport={{once: true}}
            transition={{
              ease: 'easeInOut',
              duration: 0.4,
              delay: 0.4,
            }}>
            <p>
              <strong className={styles.condi1}>조건1</strong>,
              <strong className={styles.condi2}>조건2</strong>
              <strong> 하나만 참여해도 1,500명</strong>
              에게는 커피쿠폰!
              <br />
              추첨으로 <strong className={styles.item}>닌텐도 스위치2</strong>
              까지! 절대 놓치지 마세요!
            </p>
          </motion.div>
          <motion.button
            type='button'
            className={styles.my_subs_info}
            initial={{opacity: 0, x: 50}}
            whileInView={{opacity: 1, x: 0}}
            viewport={{once: true}}
            transition={{
              ease: 'easeInOut',
              duration: 0.4,
              delay: 0.4,
            }}
            onClick={handleClickSubsBtn}>
            나의 구독 확인 후 즉시 변경
          </motion.button>
        </div>
      </div>
      {/* visual */}

      {/* tab */}
      <div className={styles.tab_area}>
        <div className={styles.inn}>
          <button
            type='button'
            className={activeTab === 1 ? styles.active : ''}
            onClick={() => handleTabClick(1)}>
            <span className={styles.ic_condi_1}>조건1</span>플러스 즉시 무료
            변경
          </button>
          <button
            type='button'
            className={activeTab === 2 ? styles.active : ''}
            onClick={() => handleTabClick(2)}>
            <span className={styles.ic_condi_2}>조건2</span>최초 플러스 자동결제
          </button>
        </div>
      </div>
      {/* tab */}

      {/* 플러스 즉시 무료 변경 */}
      <div className={styles.tab1}>
        <section className={styles.condi_1}>
          <div className={styles.inn}>
            <div className={styles.section_title}>
              <span className={styles.ic_condi_1}>조건1</span>
              <strong className={styles.plus_free}>
                즉시변경. 플러스 즉시 무료 이용!
              </strong>
            </div>
            <div className={styles.basic}>
              <p className={styles.box}>
                9/15일까지 베이직 구독 시작한 유저가
                <br />
                <strong>플러스 자동결제</strong> 즉시 변경 시
              </p>
              <i className={styles.arrow}>⬇️</i>
              <p>
                <strong>잔여 기간</strong>을 즉시 플러스로
                <br />
                <strong className={styles.free}>무료 업그레이드!</strong>
              </p>
              <img
                className={styles.img_upgrade}
                src='//res.sooplive.co.kr/eventzone/event/2025/subs-direct-change/condi1_upgrade.png'
                alt=''></img>
            </div>
            <div className={styles.free_period_info}>
              <strong className={styles.title}>
                예상 무료 플러스 기간 예시
              </strong>
              <div className={styles.img_list_box}>
                <img
                  src='//res.sooplive.co.kr/eventzone/event/2025/subs-direct-change/plus_period_ex.png'
                  alt=''
                />
              </div>
            </div>
            <ul className={styles.noti_list}>
              <li>
                베이직 정기권, 선물권, 자동결제 이용중이면 플러스 자동결제로
                즉시변경 가능합니다.
              </li>
              <li>
                30일 초과 잔여기간은 정기권으로 복구되며 남은 기간은 플러스로
                무료 이용 가능합니다.
              </li>
            </ul>
            <button
              type='button'
              className={styles.my_subs_info}
              onClick={handleClickSubsBtn}>
              나의 구독 확인 후 즉시 변경
            </button>
          </div>
        </section>
        <section className={styles.change_process}>
          <div className={styles.inn}>
            <strong className={styles.title}>구독 즉시 변경 방법</strong>
            <div className={styles.box_1}>
              <p>01. 구독하기에서 구독변경 or 변경 선택</p>
            </div>
            <div className={styles.box_2}>
              <p>02. 즉시 변경 안내 확인</p>
            </div>
            <div className={styles.box_3}>
              <p>
                03. 플러스 자동결제로 즉시변경 진행! 정기권/선물받은 이용권은
                복구 됩니다.
              </p>
            </div>
            <div className={styles.box_4}>
              <p>04. 완료! 이제 플러스로 즐기세요!</p>
            </div>
            <p className={styles.noti_msg}></p>
            <ul className={styles.noti_list}>
              <li>
                갤럭시스토어/원스토어 앱에서 아래 경로로 즉시 변경 가능합니다.
              </li>
              <li className={styles.depth2}>
                - APP &gt; MY &gt; 구독 &gt; 구독중인 스트리머 더보기 &gt; 구독
                연장하기
              </li>
              <li className={styles.depth2}>
                - APP &gt; 구독중인 스트리머 LIVE, VOD, 방송국 &gt; 구독하기
                버튼 누른 후 진행
              </li>
              <li className={styles.depth2}>
                - IOS, 구글스토어 앱, 모바일 웹 불가
              </li>
            </ul>
          </div>
        </section>
      </div>
      {/* 플러스 즉시 무료 변경 */}

      {/* 최초 플러스 자동결제 */}
      <div className={styles.tab2}>
        <section className={styles.condi_2}>
          <div className={styles.inn}>
            <div className={styles.section_title}>
              <span className={styles.ic_condi_2}>조건2</span>
              <strong className={styles.plus_free}>
                최초 플러스 자동결제!
              </strong>
            </div>
            <div className={styles.autopay}>
              <p className={styles.desc}>
                모든 스트리머 대상으로
                <br />
                <strong>플러스 자동결제 </strong>
                <strong className={styles.first}>최초 결제 시</strong>
              </p>
              <i className={styles.arrow}>⬇️</i>
              <strong className={styles.gift_txt}>경품 자동 응모!</strong>
              <ul className={styles.noti_list}>
                <li>
                  조건1, 조건2 전체 참여자 대상으로 닌텐도 스위치2 추첨과 커피
                  쿠폰 1,500명 지급
                </li>
                <li>
                  스트리머가 최초 구독 플러스 1개월 자동결제 무료 ON한 경우도
                  포함
                </li>
              </ul>
            </div>
            {deviceType === 'pc' && (
              <button
                type='button'
                className={styles.subs_benefit}
                onClick={handleClickItemBtn}>
                구독 혜택이 궁금하다면?
              </button>
            )}
          </div>
        </section>
      </div>
      {/* 최초 플러스 자동결제 */}

      {/* 유의사항 */}
      <section className={styles.noti_area}>
        <div className={styles.inn}>
          <ul>
            <li className={`${activeGuideLines === 0 ? styles.active : ''}`}>
              <div className={styles.title_box}>
                <button type='button' onClick={handleActiveGuideLine} value={0}>
                  이벤트 <em>안내사항</em>
                </button>
              </div>
              <motion.div
                className={styles.desc_box}
                animate={{
                  height: activeGuideLines === 0 ? 'auto' : 0,
                  opacity: activeGuideLines === 0 ? 1 : 0,
                }}>
                <ul>
                  <li>
                    이벤트 당첨자는 10/30(목) 이벤트 당첨자 발표페이지로
                    안내드립니다.
                  </li>
                  <li>
                    닌텐도 스위치2는 1명, 커피쿠폰은 1,500명에게 지급 됩니다.
                  </li>
                  <li>
                    이벤트 당첨자는 알림톡, 쪽지를 통하여 안내 예정이며 당첨자와
                    연락이 어려운 경우 상품 지급이 사전에 연락처 확인 및 쪽지
                    수신 거부 해제 바랍니다.
                  </li>
                  <li>
                    경품 발송을 위해 개인정보(휴대폰번호, 주소) 업데이트 및
                    광고성 정보 수신 동의가 필요합니다.
                    <br />
                    <a href={`#`} onClick={handleClickPersonalBtn}>
                      <span>개인 정보 수정하기</span>
                    </a>
                  </li>
                  <li>
                    조건1,2 동시 해당된 경우 최대 1건에 대한 기프티콘이
                    제공합니다.
                  </li>
                  <li>
                    조건1, 조건2에 대한 구독 즉시 변경, 최초 자동 결제를 많이
                    할수록 닌텐도 스위치2 경품 당첨 확률이 높습니다.
                  </li>
                </ul>
              </motion.div>
            </li>
            <li className={`${activeGuideLines === 1 ? styles.active : ''}`}>
              <div className={styles.title_box}>
                <button type='button' onClick={handleActiveGuideLine} value={1}>
                  이벤트 <em>유의사항</em>
                </button>
              </div>
              <motion.div
                className={styles.desc_box}
                initial={{height: 0, opacity: 0}}
                animate={{
                  height: activeGuideLines === 1 ? 'auto' : 0,
                  opacity: activeGuideLines === 1 ? 1 : 0,
                }}
                transition={{duration: 0.3}}
                style={{overflow: 'hidden'}}>
                <ul>
                  <li>
                    본 이벤트는 구독 자동결제, 정기권 구매 대상 이벤트며, 구독
                    선물권 구매 경우 경품 대상에서 제외됩니다.
                  </li>
                  <li>
                    소득세법 제21조 및 제84조 제2항 제3호 등에 따라 5만원을
                    초과하는 경품이나 상금의 경우 상품 금액의 22% 제세공과금을
                    지불해야 합니다.
                  </li>
                  <li>
                    당첨자에게는 제세공과금 납부에 필요한 정보를 요청드릴
                    예정이며, 정보 미제공시 당첨이 취소될 수 있습니다.
                  </li>
                  <li>
                    조건2 이벤트는 기존 구독 베이직, 플러스 자동결제 이용 중인
                    유저 (자동결제 배치 유저)는 미대상이며, 신규로 다른
                    스트리머를 구독하셔야합니다.
                  </li>
                  <li>결제 취소 및 환불 시 경품 지급대상에서 제외됩니다.</li>
                  <li>
                    경품은 경품 판매처 사정에 의해 유사금액의 다른 경품으로
                    대체될 수 있으며, 대체 되는 경우 사전 공지할 예정입니다.
                  </li>
                  <li>
                    비정상적이거나 불법적인 방법으로 이벤트에 참여하신 경우 경품
                    지급 대상에서 제외 되며, 법적인 처벌을 받을 수 있습니다.
                  </li>
                  <li>경품에 대한 실제 배송일은 당첨자를 통해 안내드립니다.</li>
                  <li>
                    본 이벤트는 당사 사정에 의하여 사전 고지 없이 내용 변경 또는
                    조기 종료될 수 있습니다.
                  </li>
                </ul>
              </motion.div>
            </li>
          </ul>
        </div>
      </section>
      {/* 유의사항 */}

      <footer className={styles.footer}>ⓒ SOOP Corp.</footer>
    </div>
  );
};

export default SubscribeDirectChange;
